import React, { useState, useEffect } from 'react';

/**
 * Order Creation Progress Overlay
 * Shows step-by-step progress during order creation to provide feedback
 * while the backend processes the order (especially Shopify API calls)
 */
// withShopifyStep: false for shops without Shopify, which never sync an order there.
const OrderCreationProgress = ({ isVisible, onComplete, withShopifyStep = true }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepStartTime, setStepStartTime] = useState(null);

  // Define the steps that occur during order creation
  const allSteps = [
    {
      id: 'validate',
      label: 'Validating order details',
      icon: '📋',
      duration: 400, // Quick step
    },
    {
      id: 'customer',
      label: 'Saving customer information',
      icon: '👤',
      duration: 600,
    },
    {
      id: 'database',
      label: 'Creating order record',
      icon: '💾',
      duration: 500,
    },
    {
      id: 'shopify',
      label: 'Syncing with Shopify',
      icon: '🛒',
      duration: 3000, // This is the slow step - show it longer
      subtext: 'This may take a few seconds...',
    },
    {
      id: 'print',
      label: 'Sending to print queue',
      icon: '🖨️',
      duration: 800,
    },
    {
      id: 'complete',
      label: 'Order created successfully!',
      icon: '✅',
      duration: 500,
    },
  ];
  const steps = withShopifyStep ? allSteps : allSteps.filter(step => step.id !== 'shopify');

  // Reset and start progress when becoming visible
  useEffect(() => {
    if (isVisible) {
      setCurrentStep(0);
      setStepStartTime(Date.now());
    }
  }, [isVisible]);

  // Auto-advance through steps based on duration
  useEffect(() => {
    if (!isVisible || currentStep >= steps.length - 1) return;

    const currentStepData = steps[currentStep];
    const timer = setTimeout(() => {
      setCurrentStep(prev => prev + 1);
      setStepStartTime(Date.now());
    }, currentStepData.duration);

    return () => clearTimeout(timer);
  }, [isVisible, currentStep, steps]);

  // Notify when complete
  useEffect(() => {
    if (currentStep === steps.length - 1 && isVisible) {
      const timer = setTimeout(() => {
        onComplete?.();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentStep, isVisible, onComplete, steps.length]);

  if (!isVisible) return null;

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-floral-600 [html[data-scheme=dark]:not([data-appearance=fable])_&]:from-floral-700 to-floral-700 px-6 py-4">
          <h3 className="text-lg font-semibold text-white flex items-center">
            <svg className="animate-spin h-5 w-5 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Creating Your Order
          </h3>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-200">
          <div
            className="h-full bg-floral-500 transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Current step display */}
        <div className="px-6 py-8">
          <div className="flex items-center justify-center mb-6">
            <div className="text-4xl animate-bounce">
              {currentStepData.icon}
            </div>
          </div>

          <p className="text-center text-lg font-medium text-gray-800 mb-2">
            {currentStepData.label}
          </p>

          {currentStepData.subtext && (
            <p className="text-center text-sm text-gray-500">
              {currentStepData.subtext}
            </p>
          )}
        </div>

        {/* Step indicators */}
        <div className="px-6 pb-6">
          <div className="flex justify-center space-x-2">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index < currentStep
                    ? 'bg-floral-500'
                    : index === currentStep
                    ? 'bg-floral-500 scale-125'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <p className="text-center text-xs text-gray-400 mt-3">
            Step {currentStep + 1} of {steps.length}
          </p>
        </div>

        {/* Step list */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
          <div className="space-y-2">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`flex items-center text-sm transition-all duration-300 ${
                  index < currentStep
                    ? 'text-floral-600'
                    : index === currentStep
                    ? 'text-gray-800 font-medium'
                    : 'text-gray-400'
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center mr-2">
                  {index < currentStep ? (
                    <svg className="w-4 h-4 text-floral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : index === currentStep ? (
                    <div className="w-2 h-2 bg-floral-500 rounded-full animate-pulse" />
                  ) : (
                    <div className="w-2 h-2 bg-gray-300 rounded-full" />
                  )}
                </span>
                <span>{step.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderCreationProgress;
